import { TableSize } from '@/components/common/table-size'
import { useSizeTableBySlug } from '@/hooks/react-query/size/use-size'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { SizeFormatter } from '@/services/formatter/__f.size'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useMemo } from 'react'

type SizeTableMaleContentProps = {
  sizeChart?: string
  productId?: string
}

export const SizeTableMaleContent = ({
  sizeChart,
  productId,
}: SizeTableMaleContentProps) => {
  const { data, isLoading } = useSizeTableBySlug(sizeChart || '')
  const t = useTranslations('product_detail')

  const tableData = useMemo(() => {
    if (!data) {
      return {
        rows: [],
        columns: [],
        matrix: [],
        image: '',
        headerTitle: '',
        leftTitle: '',
        title: '',
      }
    }

    return SizeFormatter.formatSizeTable(data)
  }, [data])

  const isJeanOrShort = useMemo(() => {
    return sizeChart?.includes('jean') || sizeChart?.includes('short')
  }, [sizeChart])

  if (isLoading) {
    return (
      <div className='py-4 text-center'>
        <h3 className='text-lg font-medium'>{t('instruction_size.loading')}</h3>
      </div>
    )
  }

  if (!tableData.rows.length) {
    return (
      <div className='py-4 text-center'>
        <h3 className='text-lg font-medium'>
          {t('instruction_size.not_found')}
        </h3>
      </div>
    )
  }

  // console.log('data', tableData)

  return (
    <div className='py-4'>
      <TableSize
        rows={tableData.rows}
        columns={tableData.columns}
        matrix={tableData.matrix}
        title={t('title.specifications')}
        className='mt-4'
        suffixCol='Size'
        colClassName='min-w-32 text-start'
        itemClassName='min-w-24'
        productId={productId}
        note={tableData.note}
      />

      <div className='my-4 rounded bg-neutral-200 px-4 py-3 font-sans text-sm leading-4.5 text-neutral-800/90'>
        <p className='font-bold'>
          {t('instruction_size.hint_between_sizes.title')}
        </p>
        <ul className='list-disc pl-4'>
          {isJeanOrShort ? (
            <>
              <li>
                {t(
                  'instruction_size.hint_between_sizes.jean_or_short.priority_weight'
                )}
              </li>
              <li>
                {t('instruction_size.hint_between_sizes.jean_or_short.example')}
              </li>
            </>
          ) : (
            <>
              <li>
                {t('instruction_size.hint_between_sizes.shirt.priority_height')}
              </li>
              <li>{t('instruction_size.hint_between_sizes.shirt.example')}</li>
            </>
          )}
          <li>{t('instruction_size.hint_between_sizes.confidence')}</li>
        </ul>
      </div>
      {tableData.image && (
        <div className='mb-4 flex justify-center'>
          <Image
            src={ImageHelper.getImageUrl(tableData.image)}
            alt={t('instruction_size.size_chart_alt')}
            width={500}
            height={300}
            className='w-full rounded-lg object-contain'
            unoptimized
          />
        </div>
      )}
    </div>
  )
}
